// src/components/steps/Step2Generate.jsx
//
// Wizard step 2 — generation in progress: the PipelineStepper plus a cancel
// button behind a confirm modal (the cancel itself is the caller's `onCancel`).
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PipelineStepper from '../ui/PipelineStepper.jsx';
import { COLORS, RADII, SEMANTIC } from '../../constants/theme.js';
import { useContentInset } from '../ui/modalArea.js';

const CancelConfirmModal = ({ onConfirm, onCancel, t }) => {
  const { t: tr } = useTranslation('wizard');
  // Centred in the page's content area (beside the sidebar), like every app modal — components/ui/modalArea.js.
  const insetLeft = useContentInset(true);
  return (
  <div
    onClick={onCancel}
    role="dialog"
    aria-modal="true"
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
      ...(insetLeft ? { paddingLeft: `${insetLeft + 24}px` } : null),
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: t.inputBg || '#1a1a2e', borderRadius: RADII.control,
        border: `1.5px solid ${SEMANTIC.errore.bg}`,
        padding: '28px 28px 24px',
        maxWidth: '420px', width: '100%',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <span style={{ fontSize: '20px' }}>⚠️</span>
        <span style={{ fontSize: '15px', fontWeight: 700, color: SEMANTIC.errore.fg }}>{tr('step2.confirm.title')}</span>
      </div>
      <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: t.text }}>
        {tr('step2.confirm.body')}
      </p>
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1, padding: '10px', borderRadius: RADII.control,
            border: `1px solid ${t.border || 'rgba(255,255,255,0.1)'}`,
            background: 'transparent', color: t.text,
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {tr('step2.confirm.continue')}
        </button>
        <button
          onClick={onConfirm}
          style={{
            flex: 1, padding: '10px', borderRadius: RADII.control,
            border: `1px solid ${SEMANTIC.errore.bg}`,
            background: SEMANTIC.errore.bg, color: SEMANTIC.errore.fg,
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {tr('step2.confirm.cancel')}
        </button>
      </div>
    </div>
  </div>
  );
};

const Step2Generate = ({ progress, color, w, t, onCancel }) => {
  const { t: tr } = useTranslation('wizard');
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!progress) return null;

  const showCancel =
    typeof onCancel === 'function' &&
    !progress.isComplete && !progress.isError && !progress.isCancelled;

  const pending = progress.cancelStatus === 'pending';
  const tooLate = progress.nonCancellable || progress.cancelStatus === 'too-late';
  const disabled = pending || tooLate;

  const label = tooLate
    ? tr('step2.cancelTooLate')
    : pending
    ? tr('step2.cancelPending')
    : tr('step2.cancelButton');

  const handleClick = () => {
    if (disabled) return;
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    onCancel();
  };

  return (
    <div style={{
      padding: '20px',
      background: t.surface,
      borderRadius: RADII.card,
      border: `1px solid ${t.border}`,
    }}>
      <PipelineStepper
        slots={progress.slots}
        activePhase={progress.activePhase}
        isComplete={progress.isComplete}
        isError={progress.isError}
        isCancelled={progress.isCancelled}
        errorMessage={progress.errorMessage}
        warningsCount={progress.warningsCount}
        slotTimings={progress.slotTimings}
        color={color}
        theme={t}
        w={w}
      />
      {showCancel && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
          <button
            onClick={handleClick}
            disabled={disabled}
            style={{
              padding: '10px 18px', borderRadius: RADII.control,
              border: `1px solid ${t.border}`,
              background: t.surface,
              color: disabled ? (t.textDim || COLORS.textTertiary) : t.textMuted,
              fontSize: '13px', fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              transition: 'all 0.2s',
            }}
          >
            {label}
          </button>
        </div>
      )}
      {confirmOpen && (
        <CancelConfirmModal
          onConfirm={handleConfirm}
          onCancel={() => setConfirmOpen(false)}
          t={t}
        />
      )}
    </div>
  );
};

export default Step2Generate;
